/* Filteren, sorteren en tellen voor de projectlijst. Puur, zodat het te toetsen is.
 * De volgorde die de lijst toont wordt bewaard (sessionStorage); de projectpagina
 * gebruikt hem voor vorige/volgende. */

import type { Signalen } from './signalen';

export const STADIA = ['verkennen', 'voorstel', 'gewonnen', 'operatie'] as const;
export type Stadium = (typeof STADIA)[number];

export interface LijstProject {
  id: string;
  name: string;
  project_number: string | null;
  stage: string | null;
  status: string | null;
  amount: number | null;
  probability: number | null;
  updated_at: string | null;
  created_at: string;
  org_id: string | null;
  organization?: { id: string; name: string } | null;
  owner?: { display_name: string | null; email: string | null; avatar_url?: string | null; avatar_bg_color?: string | null } | null;
  sharepoint_web_url?: string | null;
  sharepoint_main_url?: string | null;
}

export type Sortering = 'activiteit' | 'nummer' | 'naam' | 'waarde' | 'stadium';
export type StatusFilter = 'lopend' | 'afgesloten' | 'alles';

export interface LijstFilter {
  zoek: string;
  stadium: Stadium | 'alle';
  status: StatusFilter;
  sortering: Sortering;
  oplopend: boolean;
}

export const STANDAARD_FILTER: LijstFilter = { zoek: '', stadium: 'alle', status: 'lopend', sortering: 'activiteit', oplopend: false };

const afgesloten = (p: LijstProject) => p.status === 'verloren' || p.status === 'afgerond';

/** De laatste activiteit van een project: signalen, of anders de laatste wijziging aan het project zelf. */
export function activiteit(p: LijstProject, s: Signalen | undefined): string {
  const a = s?.laatsteActiviteit ?? '';
  const b = p.updated_at ?? p.created_at;
  return a > b ? a : b;
}

/** Projectnummer als getal voor sorteren: P09 < P18 < P40; zonder nummer achteraan. */
const nummer = (p: LijstProject) => {
  const m = /(\d+)/.exec(p.project_number ?? '');
  return m ? Number(m[1]) : -1;
};

export function passendBijZoek(p: LijstProject, zoek: string): boolean {
  const z = zoek.trim().toLowerCase();
  if (!z) return true;
  return [p.name, p.project_number, p.organization?.name].some((v) => v?.toLowerCase().includes(z));
}

export function filterStatus(p: LijstProject, status: StatusFilter): boolean {
  if (status === 'alles') return true;
  return status === 'afgesloten' ? afgesloten(p) : !afgesloten(p);
}

export function filterEnSorteer(projecten: LijstProject[], f: LijstFilter, signalen: Map<string, Signalen>): LijstProject[] {
  const uit = projecten.filter(
    (p) => passendBijZoek(p, f.zoek) && filterStatus(p, f.status) && (f.stadium === 'alle' || p.stage === f.stadium),
  );
  const richting = f.oplopend ? 1 : -1;
  const sleutel = (p: LijstProject): number | string => {
    switch (f.sortering) {
      case 'nummer': return nummer(p);
      case 'naam': return p.name.toLowerCase();
      case 'waarde': return p.amount ?? -1;
      case 'stadium': return STADIA.indexOf((p.stage ?? '') as Stadium);
      default: return activiteit(p, signalen.get(p.id));
    }
  };
  return uit.sort((a, b) => {
    const x = sleutel(a);
    const y = sleutel(b);
    if (x < y) return -1 * richting;
    if (x > y) return 1 * richting;
    return nummer(b) - nummer(a);
  });
}

export interface StadiumTelling {
  stadium: Stadium | 'alle';
  aantal: number;
  waarde: number;
}

/** Telling per stadium binnen de zoekterm en statusfilter, zodat de tabs kloppen met wat je ziet. */
export function tellingen(projecten: LijstProject[], f: Pick<LijstFilter, 'zoek' | 'status'>): StadiumTelling[] {
  const basis = projecten.filter((p) => passendBijZoek(p, f.zoek) && filterStatus(p, f.status));
  const tel = (lijst: LijstProject[]) => ({ aantal: lijst.length, waarde: lijst.reduce((s, p) => s + (p.amount ?? 0), 0) });
  return [
    { stadium: 'alle', ...tel(basis) },
    ...STADIA.map((s) => ({ stadium: s, ...tel(basis.filter((p) => p.stage === s)) })),
  ];
}

/* ── volgorde voor vorige/volgende op de projectpagina ─────────────── */

const SLEUTEL = 'projectlijst:volgorde';

export function bewaarVolgorde(ids: string[]): void {
  try {
    sessionStorage.setItem(SLEUTEL, JSON.stringify(ids));
  } catch {
    /* privévenster of geen opslag: dan valt de pagina terug op projectnummer */
  }
}

export function leesVolgorde(): string[] | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(SLEUTEL) ?? 'null');
    return Array.isArray(v) && v.every((x) => typeof x === 'string') ? v : null;
  } catch {
    return null;
  }
}

/** Vorige en volgende rond `id` in `volgorde`; null aan de randen of als id er niet in staat. */
export function buren(volgorde: string[], id: string): { vorige: string | null; volgende: string | null; positie: number; totaal: number } | null {
  const i = volgorde.indexOf(id);
  if (i === -1) return null;
  return { vorige: volgorde[i - 1] ?? null, volgende: volgorde[i + 1] ?? null, positie: i + 1, totaal: volgorde.length };
}

/** 'vandaag', 'gisteren', '3 dagen', '2 wkn', '4 mnd', of de datum. */
export function relatief(iso: string | null | undefined, nu: Date = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dagen = Math.floor((new Date(nu.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000);
  if (dagen <= 0) return 'vandaag';
  if (dagen === 1) return 'gisteren';
  if (dagen < 14) return `${dagen} dagen`;
  if (dagen < 60) return `${Math.floor(dagen / 7)} wkn`;
  if (dagen < 365) return `${Math.floor(dagen / 30)} mnd`;
  return new Intl.DateTimeFormat('nl-NL', { month: 'short', year: 'numeric' }).format(d);
}
